import React, {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {motion} from 'framer-motion';
import {Box, Text} from '@tenancy.nz/design-system';

import AccordionList from '../../components/AccordionList';
import AccordionItem from '../../components/AccordionItem';
import ClientList from '../../components/ClientList';
import ContactContent from '../../components/ContactContent';
import ContactForm from '../../components/ContactForm';
import ContentImg from '../../components/ContentImg';
import LeftRightContent from '../../components/LeftRightContent';
import Link from '../../components/Link';
import LinkButton from '../../components/LinkButton';
import Title from '../../components/Title';

import {sendEnquiry} from './contactSlice';
import {uploadFileAsync} from '../file/fileSlice';
import MessageFeature from '../message/MessageFeature';
import {showErrorMessage, showSuccessMessage} from '../message/messageSlice';
import {userState} from '../user/userSlice';

import * as consts from '../../utils/constants';
import {getCookie, getObjectProp} from '../../utils/helpers';
import mutators from '../../utils/mutators/common';

import girl from '../../assets/girl-alt.svg';

function ContactFeature() {
  const dispatch = useDispatch();
  const {token} = useSelector(userState);
  const location = useLocation();
  const [attachments, setAttachments] = useState([]);
  const [openedPanel, setOpenedPanel] = useState(null);

  const onAttachmentUpload = files => {
    files.forEach(async file => {
      if (!file.id) {
        await uploadFileAsync(file, file.path.split('.')[0], '', true).then(
          response => {
            setAttachments(prev => [...prev, response]);
          }
        );
      }
    });
  };

  const onAttachmentRemove = files => {
    setAttachments([]);
  };

  const handlePanelToggle = panel => (_, isExpanded) =>
    setOpenedPanel(isExpanded ? panel : null);

  const handleSubmit = values => {
    const attachmentsIds = attachments
      .map(attachment => attachment.id)
      .filter(attachment => !!attachment);
    const comments = `${values.comments}. Last visited URL: ${getObjectProp(
      location,
      'state.prevPath'
    )}`;
    const allValues = {...values, attachments: attachmentsIds, comments};
    return new Promise((resolve, reject) =>
      dispatch(sendEnquiry(allValues)).then(({payload}) => {
        const {error, message} = payload;
        if (error || message) {
          const errorMsg =
            getObjectProp(error, 'message') ||
            getObjectProp(message, 'message') ||
            'An unexpected error has occured, please try again.';
          dispatch(showErrorMessage(errorMsg));
          return reject(payload);
        }
        dispatch(showSuccessMessage('Your message has been sent. Thank you!'));
        setOpenedPanel(null);
        setAttachments([]);
        return resolve(payload);
      })
    );
  };

  const isLoggedIn = !!getCookie(consts.KEY_AUTHENTICATION_TOKEN) || !!token;

  const loggedInItems = [
    ...(!isLoggedIn
      ? [
          {
            details: (
              <Link
                to={consts.SLUG_FORGOTTEN}
                size="small"
                color="inherit"
                underline="always">
                Click here to reset your password
              </Link>
            ),
            title: 'Password Reset',
            markdown: false,
          },
          {
            details: (
              <Text paragraph>
                If you are having issues accessing your account, such as
                receiving a server error message when logging in, do not worry a
                simple reset should sort it out. Please go to this{' '}
                <Link
                  to={consts.SLUG_REQUEST_RESET}
                  size="small"
                  color="inherit"
                  underline="always">
                  link
                </Link>{' '}
                and enter your email address, and we will send you a link to
                reset your account. If the problem persists, contact
                info@tenant.co.nz to get this resolved.
                <br />
                <Link
                  to={consts.SLUG_REQUEST_RESET}
                  size="small"
                  color="inherit"
                  underline="always">
                  Reset Account
                </Link>
              </Text>
            ),
            title: 'Having issues with accessing your account',
            markdown: false,
          },
        ]
      : []),
  ];

  const items = [
    {
      details:
        'Please ask the property manager to send you the application form link.',
      title: 'Cannot find application form',
      markdown: true,
    },
    {
      details:
        'We cannot make changes to your tenancy application. Please contact the Property Manager directly.',
      title: 'Make changes to a tenancy application',
      markdown: true,
    },
    {
      details:
        'Please contact the property manager directly, we cannot provide updates.',
      title: 'Update on an application',
      markdown: true,
    },
    {
      details: 'Select "Delete Profile" after logging in to your account.',
      title: 'Delete my profile',
      markdown: true,
    },
    ...loggedInItems,
    {
      details: (
        <ContactForm
          mutators={mutators}
          attachments={attachments}
          onAttachmentUpload={onAttachmentUpload}
          onAttachmentRemove={onAttachmentRemove}
          onSubmit={handleSubmit}
        />
      ),
      title: 'Report problem with the website',
      markdown: false,
    },
  ];

  const renderItems = ({details, title}, i) => {
    const key = `contact_faq_panel${i}`;
    return (
      <motion.div
        key={key}
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            ease: 'easeOut',
            delay: i * 0.1,
          },
        }}
        initial={{
          opacity: 0,
          y: 25,
        }}>
        <AccordionItem
          details={details}
          title={title}
          expanded={openedPanel === key}
          onChange={handlePanelToggle(key)}
        />
      </motion.div>
    );
  };

  const content = (
    <>
      <ContactContent />
      <AccordionList
        heading="Let us know what you need help with."
        items={items}
        renderItem={renderItems}
        my={6}
      />
      <motion.div
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            ease: 'easeOut',
            delay: 0.9,
          },
        }}
        initial={{
          opacity: 0,
          y: 25,
        }}>
        <Box mt={7}>
          <Text paragraph>Do you want your information to be sent to you?</Text>
          <LinkButton
            to={consts.SLUG_REQUEST_INFO}
            color="secondary"
            variant="outlined">
            Request Data
          </LinkButton>
        </Box>
      </motion.div>
    </>
  );

  return (
    <>
      <Title text="Contact - Tenant Portal" />
      <MessageFeature />
      <LeftRightContent
        bgcolor="light"
        heading="We're happy to help."
        subheading={"We are here to help so don't hesitate to get in contact."}
        content={content}
        rightContent={<ContentImg animation="zoomIn" src={girl} />}
      />
      <ClientList />
    </>
  );
}

export default ContactFeature;
